'use strict';
import { languageService } from '../services/LanguageService';
import AccountStore from '../store/accountStore';
import { CLIENT_TYPE } from '../constants/auth/clientType';
import { LogoutTimeoutInterceptorService } from '../interceptors/logoutTimeoutInterceptor';
import Api from '../api/api';
import { history } from 'constants/router';
import baseRouteWrapper from '../routes/baseRouteWrapper';
import { LogoutModel } from '../models/LogoutModel';
import { loadSignalRService } from '../services/signalR/loader';
import { is2bapLearnersModuleUrl, is2bapProfilesModuleUrl } from '../helper/ExternalModuleHelper';
import { get2bapProfilesAuthErrorUrl } from '../modules/2bap/external/2bapProfiles/getUrls';
import { AppBannerService } from '../services/AppBannerService';

var Dispatcher = require('../dispatcher/appDispatcher');
var ActionType = require('../constants/actionType');
var AuthenticationApi = require('../api/authenticationApi');
var AppError = require('./../utils/appError');
var ModuleAction = require('./moduleAction');
var ErrorAction = require('./registerErrorAction');
var AccountAction = require('./accountAction');
var AppAction = require('./appAction');


var AuthenticationAction = {

  /** Sign in action */
  signIn: function (data, returnUrl, router) {
    var self = this;
    return AuthenticationApi.signIn(data)
      .then(function (result) {
        if (result instanceof AppError) {
          Dispatcher.dispatch({
            actionType: ActionType.AUTH_LOGIN_FAILURE,
            error: result
          });
        } else {
          Dispatcher.dispatch({
            actionType: ActionType.AUTH_LOGIN,
            token: result
          });
          self.afterReceiveToken(result);
          self.loginRedirect(router, returnUrl);
          loadSignalRService().then(service => {
            service.getInstance(result);
          });
        }
        return result;
      });
  },

  signInSso: function (data, returnUrl, router) {
    var self = this;
    return AuthenticationApi.signInSso(data)
      .then(function (result) {
        if (result instanceof AppError) {
          Dispatcher.dispatch({
            actionType: ActionType.AUTH_LOGIN_SSO_FAILURE,
            error: result
          });
        } else {
          Dispatcher.dispatch({
            actionType: ActionType.AUTH_LOGIN,
            token: result
          });
          self.afterReceiveToken(result);
          self.loginRedirect(router, returnUrl);
          loadSignalRService().then(service => {
            service.getInstance(result);
          });
        }
      });
  },

  async signIn2bapExternal(data, returnUrl, router) {
    Dispatcher.dispatch({ actionType: ActionType.AUTH_LOGIN_2BAP_EXTERNAL_START });

    try {
      const response = await AuthenticationApi.signIn2BapExternal(data);

      if (response instanceof AppError) {
        throw response;
      }

      this.afterReceiveToken(response);
      Dispatcher.dispatch({ actionType: ActionType.AUTH_LOGIN, token: response });
      await this.loginRedirect(router, returnUrl);

      Dispatcher.dispatch({ actionType: ActionType.AUTH_LOGIN_2BAP_EXTERNAL_END, error: null });
    } catch (appError) {
      if (is2bapProfilesModuleUrl(returnUrl)) {
        router.replace(get2bapProfilesAuthErrorUrl());
        return;
      }

      if (is2bapLearnersModuleUrl(returnUrl)) {
        router.replace(get2bapProfilesAuthErrorUrl());
        return;
      }

      Dispatcher.dispatch({
        actionType: ActionType.AUTH_LOGIN_2BAP_EXTERNAL_END,
        error: appError
      });
    }
  },

  /**
   * Sign in "Act as"
   * @param data impersonate user data
   * @param returnUrl url query with act as parameters
   * @param router application router
   */
  async signInActAs(data, returnUrl, router) {
    try {
      const token = await AuthenticationApi.signInActAs(data);
      Dispatcher.dispatch({
        actionType: ActionType.AUTH_LOGIN,
        token
      });
      AppBannerService.getInstance().dispose();
      AppBannerService.getInstance().init();
      loadSignalRService().then(service => {
        service.reconnect(token)
      });
      this.loginRedirect(router, returnUrl);
    } catch (error) {
      Dispatcher.dispatch({
        actionType: ActionType.AUTH_LOGIN_ACT_AS_FAILURE,
        error
      });
    }
  },

  async loginRedirect(router, returnUrl) {
    await this._loadUserData();
    router.replace("/" + (returnUrl || ""));
  },

  logoutOnError: function(isActAsMode) {
    languageService.removeCurrentLanguageFromSessionStorage(AccountStore.getAccount());
    Dispatcher.dispatch({
      actionType: ActionType.AUTH_LOGOUT,
      isActAsMode: isActAsMode
    });
  },

  // TODO: review
  redirectToLogout() {
    history.push(baseRouteWrapper.getLogout());
  },

  logout: function (isActAsMode) {
    LogoutTimeoutInterceptorService.destroy();
    languageService.removeCurrentLanguageFromSessionStorage(AccountStore.getAccount());
    window.sessionStorage.removeItem('token');
    loadSignalRService().then(service => { service.destroy() });
    AppBannerService.getInstance().dispose();
    const model = new LogoutModel();
    AuthenticationApi.logout(model)
      .then(function () {
        Dispatcher.dispatch({
          actionType: ActionType.AUTH_LOGOUT,
          isActAsMode: isActAsMode
        });
      });
  },

  /**
   * Logout of user impersonation
   */
  async logoutActAs() {
    languageService.removeCurrentLanguageFromSessionStorage(AccountStore.getAccount());
    AppBannerService.getInstance().dispose();
    try {
      await AuthenticationApi.logoutActAs();
    } finally {
      const signalRService = await loadSignalRService();
      Dispatcher.dispatch({
        actionType: ActionType.AUTH_LOGOUT_ACT_AS,
        cb: signalRService.reconnect
      });
    }
  },

  afterReceiveToken(token) {
    if (CLIENT_TYPE.shouldLogoutOnTimeout(token)) {
      const logoutTimeout = LogoutTimeoutInterceptorService.getInstance();
      logoutTimeout.registerLogoutEvent(this.redirectToLogout);
    }
  },

  async _loadUserData(){
    AppAction.toggleGlobalSpinner(true);
    await AccountAction.getAccount();
    await Promise.all([
      ModuleAction.getModuleList(),
      AppAction.getAvailableLanguages(),
      AccountAction.getPermissions()
    ]);
    AppBannerService.getInstance().init();
    AppAction.toggleGlobalSpinner(false);
  }
};

module.exports = AuthenticationAction;
